import { Link } from "react-router-dom";
import {
  Calendar,
  Clock,
  MapPin,
  Wallet,
  Users,
  GraduationCap,
  Sparkles,
  CheckCircle2,
  ArrowRight,
  BookOpen,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionHeader from "@/components/site/SectionHeader";
import ModulesAccordion from "@/components/site/ModulesAccordion";
import ProgramComponents from "@/components/site/ProgramComponents";
import PropractEmbed from "@/components/site/PropractEmbed";
import FAQList from "@/components/site/FAQList";
import TestimonialsSection from "@/components/site/TestimonialsSection";
import ImportantNote from "@/components/site/ImportantNote";
import { BlobA, BlobB } from "@/components/site/BlobBackground";
import { PRACTICE_TRACK, FAQS_PRACTICE, SITE, FIELD_WORK_CONTEXT } from "@/lib/siteConfig";

const T = PRACTICE_TRACK;

export default function PracticeTrackPage() {
  return (
    <>
      {/* HERO */}
      <section
        className="relative overflow-hidden bg-grain"
        style={{
          backgroundImage:
            "radial-gradient(60% 60% at 80% 10%, rgba(95,127,107,0.18) 0%, rgba(95,127,107,0) 60%), radial-gradient(50% 50% at 10% 0%, rgba(200,106,74,0.10) 0%, rgba(200,106,74,0) 55%)",
        }}
      >
        <BlobA className="absolute -top-24 -right-20 w-[420px] h-[420px] hidden sm:block" />
        <BlobB className="absolute -bottom-20 -left-20 w-[420px] h-[420px] hidden sm:block" />
        <div className="container-narrow relative py-16 sm:py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(95,127,107,0.18)] text-moss text-xs font-semibold tracking-wide">
                <Sparkles className="w-3.5 h-3.5" /> Advanced Practice Track · Cohort 02
              </div>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] leading-[1.05] text-ink" data-testid="course-title">
                {T.fullName}
              </h1>
              <p className="mt-2 font-display italic text-lg sm:text-xl text-moss" data-testid="course-tagline">
                {T.tagline}
              </p>
              <p className="mt-2 text-sm text-ink-muted tracking-wide">
                {SITE.courseTagline} — <span className="italic">{SITE.pullQuote}</span>
              </p>
              <p className="mt-5 text-ink-muted text-base sm:text-lg leading-[1.75] max-w-2xl" data-testid="course-description">
                {T.description}
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-3">
                <a href="#enroll">
                  <Button data-testid="course-hero-cta-button" className="bg-[#5F7F6B] hover:bg-[#506e5c] text-white rounded-xl px-6 py-6 text-base transition-colors">
                    Apply for Cohort 02
                  </Button>
                </a>
                <a href="#curriculum">
                  <Button variant="outline" className="rounded-xl px-6 py-6 text-base border-[rgba(74,63,57,0.18)] hover:bg-[rgba(95,127,107,0.10)] transition-colors">
                    See modules
                  </Button>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-paper rounded-3xl border border-[rgba(74,63,57,0.10)] shadow-[0_14px_40px_rgba(31,26,23,0.06)] p-6 sm:p-7">
                <div className="text-[11px] uppercase tracking-[0.22em] text-terracotta font-semibold">Quick facts</div>
                <h3 className="mt-1 font-display text-xl text-ink">Everything at a glance</h3>
                <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                  <Fact icon={<Calendar className="w-4 h-4" />} label="Starts" value="1 Dec 2026" />
                  <Fact icon={<Calendar className="w-4 h-4" />} label="Ends" value="28 Feb 2027" />
                  <Fact icon={<Clock className="w-4 h-4" />} label="Duration" value={T.durationLabel} />
                  <Fact icon={<BookOpen className="w-4 h-4" />} label="Hours" value={T.totalHoursLabel} />
                  <Fact icon={<MapPin className="w-4 h-4" />} label="Format" value="Hybrid" />
                  <Fact icon={<MapPin className="w-4 h-4" />} label="Field" value="NCR (optional)" />
                  <Fact icon={<Wallet className="w-4 h-4" />} label="Fee" value={`INR ${T.feeINR.toLocaleString()}`} />
                  <Fact icon={<Users className="w-4 h-4" />} label="Selection" value="Interest-based" />
                </div>
                <p className="mt-4 text-xs text-ink-muted leading-snug">
                  {T.totalHoursNote}.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section className="container-narrow py-16 sm:py-20">
        <SectionHeader eyebrow="Who it's for" title="For those moving from coursework to clinic." subtitle="Designed for postgraduate psychology students and early-career therapists ready for applied, supervised, ethical practice." testidPrefix="audience" />
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {T.audience.map((a, i) => (
            <div key={i} data-testid={`audience-card-${i + 1}`} className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-6 hover:shadow-[0_14px_40px_rgba(31,26,23,0.08)] hover:-translate-y-0.5 transition-[transform,box-shadow]">
              <div className="w-10 h-10 rounded-xl bg-[rgba(95,127,107,0.18)] text-moss flex items-center justify-center">
                <GraduationCap className="w-5 h-5" />
              </div>
              <p className="mt-4 text-ink leading-[1.6] text-[15px]">{a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAM STRUCTURE */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20">
          <SectionHeader eyebrow="Program structure" title="How the 3 months unfold." subtitle="Training + field exposure + co-counselling + supervised pro-bono client work — paced thoughtfully across the program." testidPrefix="structure" />
          <div className="mt-12">
            <ProgramComponents components={T.components} testid="practice-components" />
          </div>
        </div>
      </section>

      {/* MODULES */}
      <section id="curriculum" className="container-narrow py-16 sm:py-20">
        <SectionHeader eyebrow="Modules" title="Eight modules — advanced, applied, and held." subtitle="Tap any module to explore what we cover. Real client work begins only after foundational readiness is established." testidPrefix="modules" />
        <div className="mt-12">
          <ModulesAccordion modules={T.modules} accent="sage" testid="practice-modules" />
        </div>
        <p className="mt-8 text-sm text-ink-muted italic max-w-2xl">{T.selectionNote}</p>
      </section>

      {/* FIELD + SUPERVISION */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <DetailCard
              eyebrow="Field practice"
              title={T.fieldWork.title}
              points={T.fieldWork.points}
              accent="sage"
            />
            <DetailCard
              eyebrow="Supervision"
              title={T.supervision.title}
              points={T.supervision.points}
              accent="terracotta"
            />
          </div>
          <div className="mt-8 bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-6">
            <div className="text-[11px] uppercase tracking-[0.22em] text-moss font-semibold">{FIELD_WORK_CONTEXT.title} context</div>
            <p className="mt-2 text-ink leading-[1.75] text-[15px]">{FIELD_WORK_CONTEXT.intro}</p>
            <ul className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {FIELD_WORK_CONTEXT.points.map((p) => (
                <li key={p} className="flex gap-2.5 text-sm text-ink-muted leading-[1.6]">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* PROGRESSION NOTE */}
      <section className="container-narrow py-12">
        <div className="flex items-start gap-4 bg-[rgba(126,155,134,0.10)] border border-[rgba(95,127,107,0.22)] rounded-2xl p-6">
          <ShieldCheck className="w-6 h-6 text-moss shrink-0 mt-0.5" />
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-moss font-semibold">Pathways beyond the program</div>
            <p className="mt-2 text-ink leading-[1.75] text-[15px]">{T.progression}</p>
          </div>
        </div>
      </section>

      {/* OUTCOMES */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20">
          <SectionHeader eyebrow="What you'll walk away with" title="Applied skills, ethical grounding, lived practice." testidPrefix="outcomes" />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {T.outcomes.map((o, i) => (
              <div key={i} data-testid={`outcome-card-${i + 1}`} className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5 flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-moss shrink-0 mt-0.5" />
                <p className="text-ink leading-[1.6] text-[15px]">{o}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENROLL */}
      <section id="enroll" className="container-narrow py-16 sm:py-20">
        <SectionHeader
          eyebrow="Enroll"
          title="Apply for the Advanced Practice Track — Cohort 02."
          subtitle="Booking is handled securely through Propract. Limited seats per cohort — selection is based on interest, intent, and readiness to engage."
          testidPrefix="enroll"
        />
        <div className="mt-10">
          <PropractEmbed
            event="evt_524be14efa10"
            expert="pp_expert_2688666a6a2b"
            containerId="propract-book"
            testid="propract-embed-practice"
          />
        </div>
        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-5">
          <div>
            <div className="font-display text-lg text-ink">Want to start a conversation first?</div>
            <p className="text-sm text-ink-muted mt-1">We'd love to understand where you are in your practice journey and whether this cohort is the right fit.</p>
          </div>
          <Link to="/contact">
            <Button data-testid="practice-contact-button" className="bg-[#5F7F6B] hover:bg-[#506e5c] text-white rounded-xl">
              Talk to us
            </Button>
          </Link>
        </div>
        <ImportantNote className="mt-6" />
      </section>

      {/* TESTIMONIALS */}
      <section className="section-warm">
        <div className="container-narrow py-16 sm:py-20">
          <SectionHeader eyebrow="Voices" title="Reflections from alumni of our earlier cohorts." testidPrefix="practice-testimonials" />
          <div className="mt-10">
            <TestimonialsSection />
          </div>
        </div>
      </section>

      {/* CROSS-PROMO */}
      <section className="container-narrow py-12">
        <div className="rounded-3xl bg-paper border border-[rgba(74,63,57,0.10)] p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-terracotta font-semibold">Earlier in your journey?</div>
            <h3 className="mt-1 font-display text-xl sm:text-2xl text-ink">Take a look at the Foundation Track.</h3>
            <p className="text-sm text-ink-muted mt-1">For Grade 11–12, undergraduates, postgrad psych/social work, and curious newcomers.</p>
          </div>
          <Link to="/course">
            <Button data-testid="practice-to-foundation-link" className="bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl">
              View Foundation Track <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FAQ */}
      <section className="container-narrow py-16 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="FAQs" title="Advanced Practice Track — your questions, answered." subtitle="If you don't see your question here, message us on WhatsApp or via the contact page." testidPrefix="faq-practice" />
          </div>
          <div className="lg:col-span-7">
            <FAQList items={FAQS_PRACTICE} testidPrefix="faq-practice" />
          </div>
        </div>
      </section>
    </>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <span className="text-moss">{icon}</span>
      <div>
        <div className="text-[10.5px] uppercase tracking-[0.16em] text-ink-muted">{label}</div>
        <div className="font-display text-[15px] text-ink leading-tight">{value}</div>
      </div>
    </div>
  );
}

function DetailCard({ eyebrow, title, points, accent = "sage" }) {
  const fg = accent === "sage" ? "text-moss" : "text-terracotta";
  const dot = accent === "sage" ? "bg-sage" : "bg-terracotta";
  return (
    <div className="bg-paper rounded-2xl border border-[rgba(74,63,57,0.10)] p-6 sm:p-7">
      <div className={`text-[11px] uppercase tracking-[0.22em] font-semibold ${fg}`}>{eyebrow}</div>
      <h3 className="mt-2 font-display text-xl text-ink">{title}</h3>
      <ul className="mt-4 space-y-2">
        {points.map((p) => (
          <li key={p} className="flex gap-3 text-ink leading-[1.7] text-[15px]">
            <span className={`mt-2.5 w-1.5 h-1.5 rounded-full shrink-0 ${dot}`} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
